import React, { useState } from 'react'

import { HeatGridProps, RadarData } from '@digital-twins/slide-schemas/generated/plot-types'

import { RadarPlot } from '../radar/RadarPlot'
import { HeatGridPlot, HeatGridPlotData } from './HeatGridPlot'

/**
 * A heat-grid payload on a card or the survey tab, with the heat ↔ radar
 * toggle when (and only when) Python stamped the radar alternative
 * (plots plan Q: heat grid by default, radar a toggle). The toggle words
 * come from the host, in the viewer's language (owner decision 5).
 */

export interface HeatGridToggleWords {
  heat: string
  radar: string
}

export interface HeatGridPlotViewProps {
  plot: {
    kind: 'heat-grid'
    data: HeatGridPlotData
    props?: HeatGridProps
    alternatives?: ReadonlyArray<{ kind: string; data: unknown }>
  }
  toggleWords?: HeatGridToggleWords
  testIdPrefix?: string
  /** Prefix of the heat / radar toggle's test ids (a host with several panels passes one per panel). */
  toggleTestIdPrefix?: string
  /** Legacy stored payloads only: today's figure string and empty-cell text. */
  formatCellValue?: (value: number) => string
  emptyCellText?: string
}

function radarOf(plot: HeatGridPlotViewProps['plot']): RadarData | null {
  const alternative = (plot.alternatives ?? []).find((entry) => entry.kind === 'radar')
  return alternative ? (alternative.data as RadarData) : null
}

export function HeatGridPlotView(props: HeatGridPlotViewProps) {
  const {
    plot,
    toggleWords,
    testIdPrefix = 'heat-grid',
    toggleTestIdPrefix = 'plot-alternative',
    formatCellValue,
    emptyCellText,
  } = props
  const [view, setView] = useState<'heat-grid' | 'radar'>('heat-grid')

  const radar = radarOf(plot)
  const canToggle = radar !== null && toggleWords !== undefined

  const onPick = (next: 'heat-grid' | 'radar') => () => setView(next)

  const toggle = canToggle ? (
    <div
      data-testid={`${toggleTestIdPrefix}-toggle`}
      role="tablist"
      style={{ display: 'inline-flex', gap: 4, marginBottom: 8, alignSelf: 'flex-start' }}
    >
      {(['heat-grid', 'radar'] as const).map((kind) => (
        <button
          key={kind}
          type="button"
          role="tab"
          aria-selected={view === kind}
          data-testid={`${toggleTestIdPrefix}-${kind}`}
          onClick={onPick(kind)}
          style={{
            padding: '3px 10px',
            borderRadius: 999,
            border: '1px solid var(--slide-border-soft, #e2e2e2)',
            background: view === kind ? 'var(--slide-neutral-light, #edf2f7)' : 'transparent',
            color: 'var(--slide-text-secondary, #384242)',
            fontSize: 12,
            fontWeight: view === kind ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          {kind === 'heat-grid' ? toggleWords?.heat : toggleWords?.radar}
        </button>
      ))}
    </div>
  ) : null

  return (
    <div data-testid={`${testIdPrefix}-view`} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {toggle}
      {view === 'radar' && radar ? (
        <RadarPlot data={radar} props={{ locale: plot.props?.locale }} />
      ) : (
        <HeatGridPlot
          data={plot.data}
          props={plot.props}
          testIdPrefix={testIdPrefix}
          formatCellValue={formatCellValue}
          emptyCellText={emptyCellText}
        />
      )}
    </div>
  )
}
